import _, { filter, get, head, join, last, map, nth } from 'lodash';
import { useActiveVizFieldSelector } from '../../../common/redux/selectors/viz-selector.hook';
import { DATE_FORMATTER, Types } from '../../../common';
import { joinTwoStringsWithSpace } from '../../../common/Util';
import { defaultTimestampSlicerOptions } from '../util';
import { useSlicerSelections } from '../../discovery-context/discovery.context';
import { useAccount } from '../../../common/utilities/account';
import { useSelector } from 'react-redux';
import { VIZ_SELECTORS } from '../../../common/redux/selectors/viz-selectors';
import {
  DefaultTimePeriod,
  getDisplayText,
  TimePeriods,
} from '../../filter/relative-date-condition';
import { messages } from '../../../i18n';
import { ITimeSlicerOption, TimestampSlicerTypes } from '../interfaces';
import { useMemo } from 'react';

export const useTimestampSlicer = ({ vizId, name }) => {
  const field = useActiveVizFieldSelector({
    discoveryId: vizId,
    name,
  });

  const isTimestampSlicer = [Types.TIMESTAMP].includes(field?.attributeType);

  const _slicerSelections = useSlicerSelections(vizId);

  const slicerSelections = map(
    filter(_slicerSelections, { name }),
    ({ option }) => ({
      option,
      isSelected: true,
    }),
  );

  const slicerOption = useMemo<ITimeSlicerOption>(() => {
    if (!isTimestampSlicer) {
      return undefined;
    }
    const { option: stringifiedOption } =
      head(slicerSelections) ?? head(defaultTimestampSlicerOptions);
    const option = JSON.parse(stringifiedOption) as ITimeSlicerOption;

    return option;
  }, [isTimestampSlicer, slicerSelections]);

  const tooltipStatus = useTimestampStatus({
    slicerOption,
    isTimestampSlicer,
  });

  return {
    slicerOption,
    isTimestampSlicer,
    tooltipStatus,
  };
};

export interface IStatusStringProps {
  slicerOption?: ITimeSlicerOption;
  isTimestampSlicer: boolean;
}

export const useTimestampStatus = ({
  slicerOption,
  isTimestampSlicer,
}: IStatusStringProps): string => {
  const { type, operands = [] } = slicerOption ?? {};

  const { currentUser: { i18nPrefs = {} } = {} } = useAccount();
  const { useFiscalCalendar } = useSelector(state => ({
    useFiscalCalendar:
      VIZ_SELECTORS.hasVizDatasetFiscalCalendarSetting(state, {}) &&
      VIZ_SELECTORS.getActiveVizFiscalSetting(state, {}) === 'true',
  }));

  let timestampStatusString = '';

  if (!isTimestampSlicer) {
    return timestampStatusString;
  }

  const generateStringForRangeType = () => {
    const timePeriod = get(TimePeriods, nth(operands, 1), DefaultTimePeriod);
    const displayMessage = getDisplayText(timePeriod, useFiscalCalendar);

    return joinTwoStringsWithSpace(
      head(operands),
      get(messages, displayMessage, displayMessage),
    );
  };

  const getTooltipForType = (_type: TimestampSlicerTypes) => {
    switch (_type) {
      case TimestampSlicerTypes.IN_PAST: {
        return generateStringForRangeType();
      }
      case TimestampSlicerTypes.CURRENT_AND_PREV: {
        return generateStringForRangeType();
      }
      case TimestampSlicerTypes.SINCE_PERIOD: {
        return `${messages.slicer.since} ${DATE_FORMATTER.format(
          head(operands),
          i18nPrefs,
        )}`;
      }
      case TimestampSlicerTypes.BETWEEN_PERIOD: {
        return `${messages.slicer.between} ${join(
          [
            DATE_FORMATTER.format(head(operands), i18nPrefs),
            DATE_FORMATTER.format(last(operands), i18nPrefs),
          ],
          '-',
        )}`;
      }
      default: {
        return generateStringForRangeType();
      }
    }
  };

  timestampStatusString = getTooltipForType(type);

  return timestampStatusString;
};
